import { Box, Card, CardContent, Typography, Chip, Button, Stack, Divider } from '@mui/material'
import { FancyGoldButton } from './FancyGoldButton'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { COLOR_GREY100, COLOR_GREY200, COLOR_GREY300, COLOR_GREY500, COLOR_TERTIARY } from '../theme'
import type { CoreTier } from '../common/PricingContent'

// Shared typography for the main headline price so all three render paths
// (discounted, "From X" and the plain default) stay in visual sync.
const PRICE_FONT_SIZE = '32px'
const PRICE_FONT_WEIGHT = 600

interface PricingTierCardProps {
    tier: CoreTier
    billingCycle: 'monthly' | 'annual'
    resolveCtaHref?: (href: string) => string
    onJoinClick?: () => void
    discountInfo?: { amount: number; discount: number } | null
}

export function PricingTierCard({
    tier,
    billingCycle,
    resolveCtaHref = h => h,
    onJoinClick,
    discountInfo,
}: PricingTierCardProps) {
    const isMonthly = billingCycle === 'monthly'
    const noMonthly = tier.monthlyPrice == null
    const hasDiscount = !!discountInfo && discountInfo.discount > 0
    // const isJoinCta = tier.cta?.startsWith('Join') ?? false

    const renderPrice = () => {
        if (isMonthly && noMonthly) {
            return <Typography sx={{ fontWeight: 800, fontSize: '28px', lineHeight: 1, color: 'text.secondary' }}>Contact us</Typography>
        }
        const displayPrice = isMonthly ? tier.monthlyPrice! : tier.price
        const displayPeriod = isMonthly ? '/month' : tier.period
        if (hasDiscount) {
            return (
                <>
                    <Typography fontWeight={800} fontSize="28px" sx={{
                        color: 'text.disabled', lineHeight: 1, mr: 0.5,
                        position: 'relative',
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            left: -2, right: -2,
                            top: '45%',
                            height: '2px',
                            bgcolor: '#c0392b',
                            transform: 'rotate(-12deg)',
                        },
                    }}>
                        {displayPrice}
                    </Typography>
                    <Typography sx={{ fontWeight: PRICE_FONT_WEIGHT, fontSize: PRICE_FONT_SIZE, lineHeight: 1 }}>
                        £{discountInfo!.amount.toFixed(2)}
                    </Typography>
                    <Typography variant="caption">+VAT</Typography>
                    <Typography color="text.secondary" variant="body1">{displayPeriod}</Typography>
                </>
            )
        }
        if (displayPrice.startsWith('From ')) {
            return (
                <>
                    <Typography component="span" color="text.secondary" variant="body2" sx={{ mr: 0.25 }}>From</Typography>
                    <Typography sx={{ fontWeight: PRICE_FONT_WEIGHT, fontSize: PRICE_FONT_SIZE, lineHeight: 1 }}>{displayPrice.replace('From ', '')}</Typography>
                    <Typography variant="caption">+VAT</Typography>
                    <Typography color="text.secondary" variant="body1">{displayPeriod}</Typography>
                </>
            )
        }
        return (
            <>
                <Typography sx={{ fontWeight: PRICE_FONT_WEIGHT, fontSize: PRICE_FONT_SIZE, lineHeight: 1 }}>{displayPrice}</Typography>
                <Typography variant="caption">+VAT</Typography>
                <Typography color="text.secondary" variant="body1">{displayPeriod}</Typography>
            </>
        )
    }

    return (
        <Card sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            overflow: 'visible',
            borderColor: COLOR_GREY200,
            ...(tier.highlight && {
                borderColor: tier.color,
                borderWidth: 2,
                boxShadow: `0 8px 40px ${tier.color}30`,
            }),
        }}>
            {tier.badge && (
                <Box sx={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)' }}>
                    <Chip
                        label={tier.badge}
                        size="small"
                        sx={{ backgroundColor: tier.color, color: 'white', fontWeight: 700, px: 1 }}
                    />
                </Box>
            )}
            <CardContent sx={{ p: 4, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="caption" sx={{ color: tier.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>
                    {tier.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 1, mb: 1 }}>
                    {renderPrice()}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                    {tier.tagline}
                </Typography>
                <Box sx={{ mb: 3 }}>
                    <FancyGoldButton
                        {...(onJoinClick ? { onClick: onJoinClick } : { to: `/${tier.ctaPath}` })}
                        color={tier.color}
                        fullWidth
                        endIcon={<ArrowForwardIcon />}
                    >
                        {tier.cta}
                    </FancyGoldButton>
                </Box>
                {!(tier.compact ?? false) &&
                    <Divider sx={{ mb: 3 }} />
                }
                <Stack spacing={1.5}>
                    {tier.features.map(f => (
                        <Box key={f.text} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            {f.plus
                                ? <AddIcon sx={{ fontSize: 18, color: COLOR_TERTIARY, flexShrink: 0, mt: 0.15 }} />
                                : f.included
                                    ? <CheckIcon sx={{ fontSize: 18, color: COLOR_TERTIARY, flexShrink: 0, mt: 0.15 }} />
                                    : <CloseIcon sx={{ fontSize: 18, color: 'text.disabled', flexShrink: 0, mt: 0.15 }} />}
                            <Typography variant="body2" color={f.included ? 'text.primary' : 'text.disabled'}>
                                {f.text}
                            </Typography>
                        </Box>
                    ))}
                </Stack>
            </CardContent>
        </Card>
    )
}
